import React, { useEffect, useMemo, useState, useCallback } from "react";
import client, { apiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { rupiah, dateFmt, dateTimeFmt, todayISO } from "@/lib/format";
import { downloadCSV } from "@/lib/export";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Banknote, CheckCircle2, Upload, ReceiptText, Loader2, Download } from "lucide-react";

const StatusBadge = ({ status }) => {
  if (status === "verified") return <Badge className="bg-emerald-100 text-emerald-700">Diterima & Terverifikasi</Badge>;
  if (status === "pending") return <Badge className="bg-amber-100 text-amber-700">Diajukan</Badge>;
  return <Badge className="bg-slate-100 text-slate-600">Belum Diajukan</Badge>;
};

const Selisih = ({ value, testid }) => {
  const zero = Math.abs(value) < 1;
  const cls = zero ? "text-muted-foreground" : value > 0 ? "text-emerald-600" : "text-rose-600";
  const sign = zero ? "" : value > 0 ? "+" : "-";
  return <span className={`font-mono font-semibold ${cls}`} data-testid={testid}>{sign}{rupiah(Math.abs(value))}</span>;
};

export default function CashDeposits() {
  const { user } = useAuth();
  const { effectiveBranch } = useBranch();
  const canSubmit = ["branch_head", "super_admin"].includes(user.role);
  const canVerify = ["admin", "super_admin"].includes(user.role);

  const [start, setStart] = useState(todayISO().slice(0, 8) + "01");
  const [end, setEnd] = useState(todayISO());
  const [daily, setDaily] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(false);

  const [submitFor, setSubmitFor] = useState(null);   // daily row -> ajukan dialog
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const [verifyFor, setVerifyFor] = useState(null);   // deposit -> verifikasi dialog
  const [received, setReceived] = useState("");
  const [verifyNote, setVerifyNote] = useState("");

  const [bukti, setBukti] = useState(null);           // deposit -> bukti dialog

  const totals = useMemo(() => {
    const t = { expected: 0, submitted: 0, received: 0, varExpected: 0, varSubmitted: 0 };
    deposits.forEach((d) => {
      t.expected += d.expected_amount || 0;
      t.submitted += d.submitted_amount || 0;
      if (d.status === "verified") {
        t.received += d.received_amount || 0;
        t.varExpected += (d.received_amount || 0) - (d.expected_amount || 0);
        t.varSubmitted += (d.received_amount || 0) - (d.submitted_amount || 0);
      }
    });
    return t;
  }, [deposits]);

  const exportDeposits = () => downloadCSV(
    `rekap-setoran-${start}_sd_${end}`,
    ["Tanggal", "Cabang", "Seharusnya", "Diajukan", "Diterima", "Selisih vs Seharusnya", "Selisih vs Diajukan", "Status", "Diajukan Oleh", "Diverifikasi Oleh"],
    [
      ...deposits.map((d) => [
        d.date, d.branch_name,
        Math.round(d.expected_amount),
        Math.round(d.submitted_amount),
        d.status === "verified" ? Math.round(d.received_amount) : "",
        d.status === "verified" ? Math.round((d.received_amount || 0) - d.expected_amount) : "",
        d.status === "verified" ? Math.round((d.received_amount || 0) - d.submitted_amount) : "",
        d.status === "verified" ? "Diterima & Terverifikasi" : "Diajukan",
        d.submitted_by_name || "",
        d.verified_by_name || "",
      ]),
      ["TOTAL", "", Math.round(totals.expected), Math.round(totals.submitted), Math.round(totals.received), Math.round(totals.varExpected), Math.round(totals.varSubmitted), "", "", ""],
    ]
  );

  const branchParam = useMemo(() => (effectiveBranch ? { branch_id: effectiveBranch } : {}), [effectiveBranch]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      client.get("/cash-deposits/daily", { params: { ...branchParam, start, end } }),
      client.get("/cash-deposits", { params: { ...branchParam, start, end } }),
    ])
      .then(([a, b]) => { setDaily(a.data); setDeposits(b.data); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [branchParam, start, end]);

  useEffect(() => { load(); }, [load]);

  const openSubmit = (row) => {
    setSubmitFor(row);
    setAmount(String(Math.round(row.expected)));
    setNote("");
  };

  const doSubmit = async () => {
    if (busy) return;
    if (!amount || Number(amount) < 0) { toast.error("Isi jumlah setoran"); return; }
    setBusy(true);
    try {
      await client.post("/cash-deposits", {
        branch_id: submitFor.branch_id, date: submitFor.date,
        submitted_amount: Number(amount), note,
      });
      toast.success("Pengajuan setoran terkirim");
      setSubmitFor(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const openVerify = (dep) => {
    setVerifyFor(dep);
    setReceived(String(Math.round(dep.submitted_amount)));
    setVerifyNote("");
  };

  const doVerify = async () => {
    if (busy) return;
    if (received === "" || Number(received) < 0) { toast.error("Isi jumlah diterima"); return; }
    setBusy(true);
    try {
      await client.post(`/cash-deposits/${verifyFor.id}/verify`, {
        received_amount: Number(received), verify_note: verifyNote,
      });
      toast.success("Setoran diverifikasi");
      setVerifyFor(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 space-y-5 animate-in-up" data-testid="cash-deposits-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
          <Banknote className="h-7 w-7 text-emerald-600" /> Setoran Kas
        </h1>
        <p className="text-sm text-muted-foreground">Setoran tunai harian: hitung yang seharusnya → pengajuan Kepala Cabang → verifikasi penerimaan oleh Regional Manager.</p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-white p-4">
        <div className="space-y-1.5"><Label className="text-xs">Dari</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" data-testid="cd-filter-start" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Sampai</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" data-testid="cd-filter-end" /></div>
        <Button onClick={load} data-testid="cd-apply">Terapkan</Button>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      <Tabs defaultValue="daily">
        <TabsList>
          <TabsTrigger value="daily" data-testid="cd-tab-daily">Laporan Harian (Seharusnya)</TabsTrigger>
          <TabsTrigger value="list" data-testid="cd-tab-list">Daftar Setoran</TabsTrigger>
        </TabsList>

        {/* ---------- LAPORAN HARIAN ---------- */}
        <TabsContent value="daily" className="mt-4">
          <div className="rounded-lg border bg-white overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Tanggal</TableHead><TableHead>Cabang</TableHead>
                <TableHead className="text-center">Shift</TableHead><TableHead>Kasir</TableHead>
                <TableHead className="text-right">Seharusnya Disetor</TableHead>
                <TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {daily.map((r) => (
                  <TableRow key={`${r.branch_id}-${r.date}`} data-testid={`cd-daily-${r.branch_id}-${r.date}`}>
                    <TableCell className="whitespace-nowrap">{dateFmt(r.date)}</TableCell>
                    <TableCell className="text-sm">{r.branch_name}</TableCell>
                    <TableCell className="text-center">{r.shift_count}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.cashiers.join(", ")}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{rupiah(r.expected)}</TableCell>
                    <TableCell><StatusBadge status={r.deposit?.status} /></TableCell>
                    <TableCell className="text-right">
                      {!r.deposit && canSubmit && (
                        <Button size="sm" onClick={() => openSubmit(r)} data-testid={`cd-submit-btn-${r.branch_id}-${r.date}`}>
                          <Upload className="mr-1 h-3.5 w-3.5" /> Ajukan Setoran
                        </Button>
                      )}
                      {r.deposit && (
                        <Button size="sm" variant="outline" onClick={() => setBukti(r.deposit)} data-testid={`cd-view-btn-${r.branch_id}-${r.date}`}>
                          <ReceiptText className="mr-1 h-3.5 w-3.5" /> Lihat
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && !daily.length && <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">Belum ada shift yang ditutup pada periode ini</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ---------- DAFTAR SETORAN ---------- */}
        <TabsContent value="list" className="mt-4">
          <div className="mb-3 flex justify-end">
            <Button variant="outline" size="sm" onClick={exportDeposits} disabled={!deposits.length} data-testid="cd-export-btn"><Download className="mr-2 h-4 w-4" /> Unduh Excel</Button>
          </div>
          <div className="rounded-lg border bg-white overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Tanggal</TableHead><TableHead>Cabang</TableHead>
                <TableHead className="text-right">Seharusnya</TableHead>
                <TableHead className="text-right">Diajukan</TableHead>
                <TableHead className="text-right">Diterima</TableHead>
                <TableHead className="text-right">Selisih (vs seharusnya)</TableHead>
                <TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {deposits.map((d) => (
                  <TableRow key={d.id} data-testid={`cd-row-${d.id}`}>
                    <TableCell className="whitespace-nowrap">{dateFmt(d.date)}</TableCell>
                    <TableCell className="text-sm">{d.branch_name}</TableCell>
                    <TableCell className="text-right font-mono">{rupiah(d.expected_amount)}</TableCell>
                    <TableCell className="text-right font-mono">{rupiah(d.submitted_amount)}</TableCell>
                    <TableCell className="text-right font-mono">{d.status === "verified" ? rupiah(d.received_amount) : "—"}</TableCell>
                    <TableCell className="text-right">{d.status === "verified" ? <Selisih value={(d.received_amount || 0) - d.expected_amount} testid={`cd-var-${d.id}`} /> : "—"}</TableCell>
                    <TableCell><StatusBadge status={d.status} /></TableCell>
                    <TableCell className="text-right">
                      {d.status === "pending" && canVerify
                        ? <Button size="sm" onClick={() => openVerify(d)} data-testid={`cd-verify-btn-${d.id}`}><CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Verifikasi</Button>
                        : <Button size="sm" variant="outline" onClick={() => setBukti(d)} data-testid={`cd-bukti-btn-${d.id}`}><ReceiptText className="mr-1 h-3.5 w-3.5" /> Bukti</Button>}
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && !deposits.length && <TableRow><TableCell colSpan={8} className="py-10 text-center text-muted-foreground">Belum ada pengajuan setoran</TableCell></TableRow>}
              </TableBody>
              {deposits.length > 0 && (
                <tfoot>
                  <TableRow className="border-t-2 bg-slate-50 font-semibold" data-testid="cd-total-row">
                    <TableCell colSpan={2}>TOTAL ({deposits.length})</TableCell>
                    <TableCell className="text-right font-mono" data-testid="cd-total-expected">{rupiah(totals.expected)}</TableCell>
                    <TableCell className="text-right font-mono" data-testid="cd-total-submitted">{rupiah(totals.submitted)}</TableCell>
                    <TableCell className="text-right font-mono" data-testid="cd-total-received">{rupiah(totals.received)}</TableCell>
                    <TableCell className="text-right"><Selisih value={totals.varExpected} testid="cd-total-var" /></TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                </tfoot>
              )}
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* ---------- DIALOG: AJUKAN SETORAN ---------- */}
      <Dialog open={!!submitFor} onOpenChange={(o) => !o && setSubmitFor(null)}>
        <DialogContent data-testid="cd-submit-dialog">
          <DialogHeader>
            <DialogTitle>Ajukan Setoran — {submitFor && dateFmt(submitFor.date)}</DialogTitle>
            <DialogDescription>{submitFor?.branch_name} · {submitFor?.shift_count} shift ditutup</DialogDescription>
          </DialogHeader>
          {submitFor && (
            <div className="space-y-4">
              <div className="rounded-lg bg-slate-50 p-3 text-sm">
                Seharusnya disetor: <span className="font-mono font-semibold text-primary" data-testid="cd-submit-expected">{rupiah(submitFor.expected)}</span>
              </div>
              <div className="space-y-1.5">
                <Label>Jumlah yang Diserahkan</Label>
                <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="cd-submit-amount" />
                {amount !== "" && <p className="text-xs">Selisih vs seharusnya: <Selisih value={Number(amount) - submitFor.expected} testid="cd-submit-variance" /></p>}
              </div>
              <div className="space-y-1.5"><Label>Catatan</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="opsional" data-testid="cd-submit-note" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubmitFor(null)}>Batal</Button>
            <Button onClick={doSubmit} disabled={busy} data-testid="cd-submit-confirm">{busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Mengirim…</> : "Kirim Pengajuan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- DIALOG: VERIFIKASI ---------- */}
      <Dialog open={!!verifyFor} onOpenChange={(o) => !o && setVerifyFor(null)}>
        <DialogContent data-testid="cd-verify-dialog">
          <DialogHeader>
            <DialogTitle>Verifikasi Penerimaan — {verifyFor && dateFmt(verifyFor.date)}</DialogTitle>
            <DialogDescription>{verifyFor?.branch_name} · diajukan oleh {verifyFor?.submitted_by_name}</DialogDescription>
          </DialogHeader>
          {verifyFor && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 rounded-lg bg-slate-50 p-3 text-sm">
                <div><div className="text-xs text-muted-foreground">Seharusnya</div><div className="font-mono font-semibold">{rupiah(verifyFor.expected_amount)}</div></div>
                <div><div className="text-xs text-muted-foreground">Diajukan</div><div className="font-mono font-semibold">{rupiah(verifyFor.submitted_amount)}</div></div>
              </div>
              <div className="space-y-1.5">
                <Label>Jumlah yang Diterima</Label>
                <Input type="number" value={received} onChange={(e) => setReceived(e.target.value)} data-testid="cd-verify-received" />
                {received !== "" && (
                  <p className="text-xs">
                    Selisih vs seharusnya: <Selisih value={Number(received) - verifyFor.expected_amount} testid="cd-verify-var-expected" />
                    {" · "}vs diajukan: <Selisih value={Number(received) - verifyFor.submitted_amount} testid="cd-verify-var-submitted" />
                  </p>
                )}
              </div>
              <div className="space-y-1.5"><Label>Catatan Verifikasi</Label><Input value={verifyNote} onChange={(e) => setVerifyNote(e.target.value)} placeholder="opsional" data-testid="cd-verify-note" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setVerifyFor(null)}>Batal</Button>
            <Button onClick={doVerify} disabled={busy} data-testid="cd-verify-confirm">{busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Memproses…</> : "Terima & Verifikasi"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- DIALOG: BUKTI PENERIMAAN ---------- */}
      <Dialog open={!!bukti} onOpenChange={(o) => !o && setBukti(null)}>
        <DialogContent data-testid="cd-bukti-dialog">
          <DialogHeader>
            <DialogTitle>Bukti Setoran — {bukti && dateFmt(bukti.date)}</DialogTitle>
            <DialogDescription>{bukti?.branch_name}</DialogDescription>
          </DialogHeader>
          {bukti && (
            <div className="space-y-3 text-sm">
              <Card className="p-4 space-y-2">
                <Row label="Seharusnya disetor" value={rupiah(bukti.expected_amount)} />
                <Row label="Diajukan (diserahkan)" value={rupiah(bukti.submitted_amount)} sub={`oleh ${bukti.submitted_by_name} · ${dateTimeFmt(bukti.submitted_at)}`} />
                {bukti.note && <Row label="Catatan pengajuan" value={bukti.note} />}
                <div className="border-t pt-2" />
                {bukti.status === "verified" ? (
                  <>
                    <Row label="Diterima & diverifikasi" value={rupiah(bukti.received_amount)} sub={`oleh ${bukti.verified_by_name} · ${dateTimeFmt(bukti.verified_at)}`} strong />
                    <Row label="Selisih vs seharusnya" node={<Selisih value={(bukti.received_amount || 0) - bukti.expected_amount} />} />
                    <Row label="Selisih vs diajukan" node={<Selisih value={(bukti.received_amount || 0) - bukti.submitted_amount} />} />
                    {bukti.verify_note && <Row label="Catatan verifikasi" value={bukti.verify_note} />}
                  </>
                ) : (
                  <div className="rounded bg-amber-50 p-2 text-xs text-amber-700" data-testid="cd-bukti-pending">Menunggu verifikasi Regional Manager.</div>
                )}
              </Card>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBukti(null)}>Tutup</Button>
            {bukti && <Button onClick={() => window.print()} data-testid="cd-bukti-print"><ReceiptText className="mr-2 h-4 w-4" /> Cetak</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print target (thermal 58mm) untuk Bukti Setoran */}
      {bukti && (
        <div id="receipt-print" className="hidden print:block">
          <div style={{ fontFamily: "monospace" }}>
            <div style={{ textAlign: "center", fontWeight: 700 }}>BUKTI SETORAN KAS</div>
            <div style={{ textAlign: "center" }}>{bukti.branch_name}</div>
            <div style={{ textAlign: "center" }}>{dateFmt(bukti.date)}</div>
            <div style={{ borderTop: "1px dashed #000", margin: "4px 0" }} />
            <PrintRow k="Seharusnya" v={rupiah(bukti.expected_amount)} />
            <PrintRow k="Diajukan" v={rupiah(bukti.submitted_amount)} />
            <div style={{ fontSize: "8px" }}>oleh {bukti.submitted_by_name}</div>
            {bukti.note ? <div style={{ fontSize: "8px" }}>Catatan: {bukti.note}</div> : null}
            <div style={{ borderTop: "1px dashed #000", margin: "4px 0" }} />
            {bukti.status === "verified" ? (
              <>
                <PrintRow k="Diterima" v={rupiah(bukti.received_amount)} />
                <div style={{ fontSize: "8px" }}>oleh {bukti.verified_by_name}</div>
                <div style={{ fontSize: "8px" }}>{dateTimeFmt(bukti.verified_at)}</div>
                <PrintRow k="Selisih vs seharusnya" v={rupiahSigned((bukti.received_amount || 0) - bukti.expected_amount)} />
                <PrintRow k="Selisih vs diajukan" v={rupiahSigned((bukti.received_amount || 0) - bukti.submitted_amount)} />
                {bukti.verify_note ? <div style={{ fontSize: "8px" }}>Verifikasi: {bukti.verify_note}</div> : null}
              </>
            ) : (
              <div style={{ textAlign: "center" }}>-- MENUNGGU VERIFIKASI --</div>
            )}
            <div style={{ borderTop: "1px dashed #000", margin: "4px 0" }} />
            <div style={{ textAlign: "center", fontSize: "8px" }}>Dicetak {dateTimeFmt(new Date().toISOString())}</div>
          </div>
        </div>
      )}
    </div>
  );
}

const rupiahSigned = (v) => (Math.abs(v) < 1 ? rupiah(0) : (v > 0 ? "+" : "-") + rupiah(Math.abs(v)));

const PrintRow = ({ k, v }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: "4px" }}>
    <span>{k}</span><span>{v}</span>
  </div>
);

const Row = ({ label, value, sub, node, strong }) => (
  <div className="flex items-start justify-between gap-3">
    <div className="text-muted-foreground">{label}</div>
    <div className="text-right">
      {node || <div className={`font-mono ${strong ? "text-base font-bold text-emerald-700" : "font-semibold"}`}>{value}</div>}
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  </div>
);
